import { ChevronLeftOutlineIcon, Icon } from '@fountain/ripple/icon';
import { Grid, Link } from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import {
  makeSelectEnforceBrandBasedPermissions,
  makeSelectPolicies,
} from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';

interface BackToCompanySettingsProps {
  /**
   * What the link reads. Defaults to the bare "Back" every consumer has used
   * since this was extracted; pass a message that names the destination when a
   * design calls for one ("Back to settings"). The destination itself is not
   * configurable — it is always company settings, brands for a brand-scoped
   * user — so a label that names somewhere else would be a lie.
   */
  label?: MessageDescriptor;
  /**
   * Who may stay on the page rendering this link. Defaults to account
   * management, which is what company settings itself requires and what every
   * consumer relied on before this prop existed.
   *
   * A page reachable on a narrower permission passes its own answer, so this
   * component stops redirecting the audience that page was opened for. It
   * governs the redirect only — the link's destination still branches on
   * `manage_account`, because that branch is about brand scoping rather than
   * page access.
   */
  authorized?: boolean;
}

export const BackToCompanySettings = ({
  label = globalMessages.back,
  authorized,
}: BackToCompanySettingsProps) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();

  const { accountSlug } = useParams<{ accountSlug: string }>();

  const policies = useSelector(makeSelectPolicies());
  const { manage_account: manageAccount } = policies;

  const redirectUnauthorizedUser = useCallback(() => {
    history.push(`/${accountSlug}/openings`);
    dispatch(addMessageAction(intl.formatMessage(messages.noAccess), 'error'));
  }, [accountSlug, dispatch, history, intl]);

  const mayStay = authorized ?? manageAccount;

  useEffect(() => {
    if (!mayStay) {
      redirectUnauthorizedUser();
    }
  }, [mayStay, redirectUnauthorizedUser]);

  const enforceBrandBasedPermissions = useSelector(
    makeSelectEnforceBrandBasedPermissions(),
  );

  return (
    <Grid item className={styles.backToSettings}>
      <Link
        color="secondary"
        href={
          !manageAccount && enforceBrandBasedPermissions
            ? `${REACT_APP_MONOLITH_BASE_URL}/${accountSlug}/brands`
            : `${REACT_APP_MONOLITH_BASE_URL}/${accountSlug}/account/edit`
        }
      >
        <Icon
          icon={ChevronLeftOutlineIcon}
          size="xs"
          color="neutral-400"
          className={styles.leftArrow}
        />
        <FormattedMessage {...label} />
      </Link>
    </Grid>
  );
};
